import { gql } from "@codesandbox/api";
import { initPitcherClient } from "@codesandbox/pitcher-client";
import { RepositoryNameInput } from "components/Git/RepositoryNameInput";
import { useEnvironmentInterface } from "environment-interface";
import {
  env,
  getDevPitcherManagerURL,
  getUsePitcherManagerThroughApi,
} from "environment-interface/env";
import { useEditor } from "features/editor/Feature";
import { useAddStatusNotification } from "features/notifications/hooks";
import type { SandboxEditor } from "features/types/editor";
import { usePitcher } from "features/utils/usePitcher";
import type { WorkspaceContentFragment as Workspace } from "gql-types/documents/web-client";
import { useCreateRepository } from "hooks/api/useCreateRepository";
import { useCsbApi } from "hooks/api/useCsbApi";
import { hasSandboxPermission } from "isomorphic/permission";
import { getSandboxTitleAndDescription, withBasePath } from "isomorphic/utils";
import {
  Avatar,
  Dialog,
  Button,
  Icon,
  Text,
  Stack,
  styled,
  Select,
  Badge,
} from "prism-react";
import { getPitcherInstance } from "queries/pitcher";
import { useState } from "react";
import type { FormEventHandler } from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { paths } from "utils/router";
import { getIsPro } from "utils/subscription";

const StyledLabel = styled(Text, {
  marginBottom: "$2",
  display: "inline-block",
});

const StyledForm = styled("form", {
  width: "100%",
  height: "100%",
});

interface ConvertToRepositoryFormProps {
  sandbox: SandboxEditor;
  activeWorkspace: Workspace;
  workspaces: Workspace[];
  organizationName: string;
  onDismiss: () => void;
}

export const ConvertToRepositoryForm: React.FC<
  ConvertToRepositoryFormProps
> = ({ sandbox, activeWorkspace, workspaces, onDismiss, organizationName }) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(
    activeWorkspace.id,
  );
  const { csbApi } = useEnvironmentInterface();

  const [privacySetting, setPrivacySetting] = useState("private");
  const [isFormValid, setIsFormValid] = useState(true);
  const pitcher = usePitcher();
  const [loading, setLoading] = useState(false);

  const api = useCsbApi();
  const editor = useEditor();
  const track = useAnalytics();
  const [createRepositoryStatus, createRepository] = useCreateRepository();
  const addStatusNotification = useAddStatusNotification();

  const hasSandboxAuthorization =
    editor.type === "sandbox" &&
    hasSandboxPermission(editor.authorization, "write_code");

  const importRepository = gql.createMutation(
    "importProject",
    ({
      owner,
      repo,
      workspaceId,
      provider,
    }: {
      owner: string;
      repo: string;
      workspaceId: string;
      provider: gql.GitProvider.GITHUB;
    }) => ({
      importProject: [
        {
          provider,
          owner,
          name: repo,
          team: workspaceId,
        },
        {
          id: true,
        },
      ],
    }),
  );

  const { title } = getSandboxTitleAndDescription(sandbox);

  function navigateToRepository(
    owner: string,
    name: string,
    defaultBranch: string,
    importFlag = true,
  ) {
    document.location.href = withBasePath(
      env,
      paths.editor.branch(
        owner,
        name,
        defaultBranch,
        selectedWorkspaceId,
        importFlag,
      ),
    );
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    setLoading(true);

    e.preventDefault();
    const repositoryName = e.currentTarget.repositoryName.value as string;
    const selectedPrivacy = e.currentTarget.privacySetting.value as string;

    try {
      track("export_to_github");

      const shouldForkDevbox =
        editor.type === "sandbox" && editor.isCloud && !hasSandboxAuthorization;

      if (editor.type === "sandbox" && editor.isCloud) {
        const isPrivate = selectedPrivacy === "private";

        const newRepository = await (shouldForkDevbox
          ? Promise.all([
              createRepository({
                isPrivate,
                name: repositoryName,
                organization: organizationName,
              }),
              api.post<{
                data: {
                  id: string;
                  updatedAt: string;
                  alias: string;
                  team?: {
                    id: string;
                  };
                };
              }>({
                path: editor.id.startsWith("github/")
                  ? `/v1/sandboxes/fork/${editor.id}`
                  : `/v1/sandboxes/${editor.id}/fork`,
                data: {
                  team_id: selectedWorkspaceId,
                  v2: true,
                },
              }),
            ]).then(([newRepository, { data: sandboxData }]) =>
              initPitcherClient(
                {
                  instanceId: sandboxData.id,
                  seamlessFork: false,
                  requestPitcherInstance: async (branchId) => {
                    const pitcherManagerResponse = await getPitcherInstance(
                      csbApi!,
                      {},
                      {
                        branchId,
                        pitcherManagerURL: getUsePitcherManagerThroughApi()
                          ? getDevPitcherManagerURL() ?? ""
                          : env.PUBLIC_PITCHER_MANAGER_URL,
                        usePitcherManagerThroughApi:
                          getUsePitcherManagerThroughApi(),
                      },
                    );
                    return pitcherManagerResponse;
                  },
                  subscriptions: {
                    client: {
                      status: true,
                    },
                    file: {
                      status: true,
                      selection: true,
                      ot: true,
                    },
                    fs: {
                      operations: true,
                    },
                    git: {
                      status: true,
                      operations: true,
                    },
                    port: {
                      status: true,
                    },
                    setup: {
                      progress: true,
                    },
                    shell: {
                      status: true,
                    },
                    system: {
                      metrics: true,
                    },
                  },
                  appId: "codesandbox",
                  onFocusChange: () => () => {},
                },
                () => {},
              ).then((devboxPitcher) =>
                devboxPitcher.clients.git
                  .pushToRemote(
                    newRepository.remoteUrl,
                    newRepository.defaultBranch,
                    true,
                  )
                  .then(() => {
                    devboxPitcher.dispose();

                    // WE do not need the intermediate Devbox anymore, let's just delete it
                    api.delete({
                      path: `/v1/sandboxes/${sandboxData.id}`,
                    });

                    return newRepository;
                  }),
              ),
            )
          : createRepository({
              isPrivate,
              name: repositoryName,
              organization: organizationName,
            }).then((newRepository) =>
              pitcher.clients.git
                .pushToRemote(
                  newRepository.remoteUrl,
                  newRepository.defaultBranch,
                  true,
                )
                .then(() => newRepository),
            ));

        await api.gql.query(importRepository, {
          owner: newRepository.owner,
          repo: newRepository.name,
          workspaceId: selectedWorkspaceId,
          provider: gql.GitProvider.GITHUB,
        });

        // Don't use router.push here, because Vite will expect that
        // we remain on the sandbox page, even though we're going to a new
        // repo page.
        navigateToRepository(
          newRepository.owner,
          newRepository.name,
          newRepository.defaultBranch,
          shouldForkDevbox,
        );
      } else {
        const iframe = document.createElement("iframe");

        const data = await new Promise<{
          sandbox: Array<{
            content: string;
            isBinary: boolean;
            path: string;
          }>;
        }>((resolve, reject) => {
          const sandboxPromise = api.rest.sandboxes.get(editor.id);

          // eslint-disable-next-line
          const onMessageEvent = async (event: MessageEvent<any>) => {
            switch (event.data.type) {
              case "create-repo-files-ready": {
                const sandbox = await sandboxPromise;

                iframe.contentWindow?.postMessage(
                  {
                    type: "create-repo-files",
                    sandbox,
                  },
                  "*",
                );
                break;
              }
              case "create-repo-files-success": {
                window.removeEventListener("message", onMessageEvent);

                resolve(event.data.message);
                break;
              }
              case "create-repo-files-error": {
                window.removeEventListener("message", onMessageEvent);
                reject(event.data.message);
                break;
              }
            }
          };

          window.addEventListener("message", onMessageEvent);

          iframe.src = "https://codesandbox.io/standalone/create-repo-files";

          document.body.appendChild(iframe);
        });

        document.body.removeChild(iframe);

        const response = await api.post<{
          username: string;
          repo: string;
          branch: string;
        }>({
          path: `/v1/sandboxes/${editor.id}/git/repo/${repositoryName}`,
          data: {
            sandbox: data.sandbox.map(({ content, path, isBinary }) => ({
              content,
              path,
              is_binary: isBinary,
            })),
          },
        });

        await api.gql.query(importRepository, {
          owner: organizationName,
          repo: repositoryName,
          workspaceId: selectedWorkspaceId,
          provider: gql.GitProvider.GITHUB,
        });

        navigateToRepository(response.username, response.repo, response.branch);
      }
    } catch (error) {
      if (error instanceof Error) {
        addStatusNotification({
          message: `Failed to create repository: ${error.message}`,
          status: "error",
        });
      } else {
        addStatusNotification({
          message: `Something went wrong, please try again`,
          status: "error",
        });
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  const disabledButton = !isFormValid;
  const isLoading = loading || createRepositoryStatus.state === "loading";

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Stack css={{ height: "100%", minHeight: "400px" }}>
        <Stack css={{ width: "100%", height: "100%" }} gap={8}>
          <Dialog.Title>Create a repository</Dialog.Title>

          <Stack>
            <StyledLabel as="label">Repository name</StyledLabel>
            <RepositoryNameInput
              defaultValue={title}
              onValidation={(validation) => {
                if (validation.status === "valid") {
                  setIsFormValid(true);
                } else {
                  setIsFormValid(false);
                }
              }}
              organizationName={organizationName}
            />
            <Stack
              css={{
                alignItems: "flex-start",
                color: "$informative-info-base",
              }}
              gap={1}
              horizontal
            >
              <Icon size={4} type="circleBang" />
              <Text>
                The repository will be created inside your personal GitHub
                account ({organizationName}).
              </Text>
            </Stack>
          </Stack>

          <Stack>
            <StyledLabel as="label">CodeSandbox workspace</StyledLabel>
            <input
              name="selectedWorkspace"
              type="hidden"
              value={selectedWorkspaceId}
            />

            <Select
              defaultValue={selectedWorkspaceId}
              onValueChange={setSelectedWorkspaceId}
              variant="solid"
            >
              {workspaces.map((workspace) => {
                return (
                  <Select.Item key={workspace.id} value={workspace.id}>
                    <Stack horizontal>
                      <Avatar
                        name={workspace.name}
                        size="s"
                        src={workspace.avatarUrl}
                      />
                      <Text> {workspace.name}</Text>

                      {getIsPro(workspace) ? (
                        <Badge variant="accentPrimary">Pro</Badge>
                      ) : null}
                    </Stack>
                  </Select.Item>
                );
              })}
            </Select>
          </Stack>

          <Stack>
            <StyledLabel as="label">Privacy settings</StyledLabel>
            <input name="privacySetting" type="hidden" value={privacySetting} />

            <Select
              onValueChange={setPrivacySetting}
              value={privacySetting}
              variant="solid"
            >
              <Select.Item value="private">Private</Select.Item>
              <Select.Item value="public">Public</Select.Item>
            </Select>
          </Stack>
        </Stack>

        <Stack css={{ marginTop: "$6", justifyContent: "flex-end" }} horizontal>
          {isLoading && (
            <Stack
              css={{
                alignItems: "flex-start",
                flex: 1,
              }}
              horizontal
            >
              <Icon size={4} type="progress" />
              <Text>
                Creating the repository on GitHub will take about 30 seconds.
              </Text>
            </Stack>
          )}

          <Dialog.Close asChild>
            <Button onClick={onDismiss} variant="secondary">
              Cancel
            </Button>
          </Dialog.Close>

          <Button
            disabled={disabledButton}
            loading={isLoading}
            type="submit"
            variant="primary"
          >
            Create repository
          </Button>
        </Stack>
      </Stack>
    </StyledForm>
  );
};
